import type { ChoreoLink } from '../api/calendar'
import type { ContentMap } from '../api/content'
import { text } from '../api/content'
import { choreoFileHref } from '../api/choreoLinks'

/**
 * The practice music and videos on one session (D-541, D-596, D-668).
 *
 * <b>One composition, two screens.</b> The parent portal and the week grid
 * rendered this list separately and identically, which is the shape the
 * invariant warns about — and it bit immediately: the uploaded-file half
 * (D-668) would have had to be written twice, in two files, by hand, and the
 * second copy would never have received the first one's fixes.
 *
 * A new tab rather than an embed, for a link and a file alike: neither screen
 * is a video player, and the browser's own player already scrubs, slows and
 * loops — which is exactly what the family said it wanted from YouTube.
 */
export function PracticeLinks({
  classId,
  musicUrl,
  choreoLinks,
  content,
}: {
  /** Whose videos these are — a file is fetched through its class, never on its own. */
  classId: string
  musicUrl: string | null | undefined
  choreoLinks: ChoreoLink[] | undefined
  content: ContentMap | undefined
}) {
  const links = choreoLinks ?? []
  if (!musicUrl && links.length === 0) return null

  return (
    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11.5px]">
      {/*
        The music first (D-596): it is the folder the classroom tablet opens,
        and `asked`, the family wants it for the same reason - "that way they
        can practice with that music and/or the videos".
      */}
      {musicUrl && (
        <a
          href={musicUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-accent underline underline-offset-2 wrap-anywhere"
        >
          {text(content, 'portal.music.link', 'Practice music')}
        </a>
      )}

      {/*
        One link per part of the routine, in the teacher's order. The studio's
        word for the link is the content block; the part is the teacher's own
        label, or a number when she gave none.
      */}
      {links.map((link, i) => {
        const href = link.mediaAssetId ? choreoFileHref(classId, link.mediaAssetId) : link.url
        if (!href) return null

        return (
          <a
            key={link.mediaAssetId ?? link.url ?? i}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-accent underline underline-offset-2 wrap-anywhere"
          >
            {text(content, 'portal.choreo.link', 'Practice video')}
            {links.length > 1 && <> &middot; {link.label ?? i + 1}</>}
          </a>
        )
      })}
    </div>
  )
}
